import * as React from "react";
import { Link } from "react-router-dom";
import { usePostFlowContext } from "@/components/PostDialogs/SharedPostFlowContext";
import { Button, Popover } from "@/components/ui";
import {
  DEFAULT_REVIEW_URL_STATE,
  serializeCoachReviewSearch,
} from "@/containers/Coach/Reputation/Reviews/components/reviewFiltersSearchParams";
import { URLS } from "@/utils/routes";
import { useShareFlow } from "./ShareFlowProvider";
import { ReviewRowSkeleton, ReviewSummaryLine } from "./ReviewPickerRow";

/** From the library: the review the post is about, starting with the player's best one. */
export const LeadReviewPicker: React.FC = () => {
  const flow = useShareFlow();
  const { onBehalfOf, effectivePlayer } = usePostFlowContext();
  const [open, setOpen] = React.useState(false);
  const leadId = flow.reviewIds[0];
  const lead = flow.postableReviews.find(r => Number(r.id) === leadId);

  if (flow.postableLoading) {
    return (
      <div className="flex flex-col gap-2" data-testid="lead-review-loading">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Review</span>
        <ReviewRowSkeleton numbered={false} />
      </div>
    );
  }

  if (flow.postableReviews.length === 0) {
    if (onBehalfOf) {
      const name = effectivePlayer?.fullName || "this player";
      const reviewsUrl = `${URLS.coach.reputation.reviews}?${serializeCoachReviewSearch({
        ...DEFAULT_REVIEW_URL_STATE,
        playerId: flow.playerId,
      })}`;
      return (
        <p className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-500">
          No reviews are ready to share for {name} yet.{" "}
          <Link to={reviewsUrl} className="font-semibold text-primary hover:underline" onClick={flow.close}>
            See their reviews
          </Link>
        </p>
      );
    }
    return (
      <p className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-500">
        You don't have reviews ready to share yet.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="lead-review">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Review</span>
        {flow.mode === "single" && flow.postableReviews.length > 1 && (
          <Popover open={open} onOpenChange={setOpen}>
            <Popover.Trigger
              render={
                <Button variant="link" size="sm" className="px-0">
                  Change
                </Button>
              }
            />
            <Popover.Content className="w-[min(90vw,420px)] p-2">
              <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto">
                {flow.postableReviews.map(review => (
                  <li key={review.id}>
                    <button
                      type="button"
                      aria-pressed={Number(review.id) === leadId}
                      className="w-full rounded-lg p-2 text-left hover:bg-slate-50 cursor-pointer"
                      onClick={() => {
                        flow.selectLeadReview(Number(review.id));
                        setOpen(false);
                      }}
                    >
                      <ReviewSummaryLine review={review} />
                    </button>
                  </li>
                ))}
              </ul>
            </Popover.Content>
          </Popover>
        )}
      </div>
      {lead && (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <ReviewSummaryLine review={lead} />
        </div>
      )}
    </div>
  );
};
